/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable max-lines */
import { LoadingSpinner, Text, type UseModalState } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { FullScreenDialog } from "@src/appV2/lib";
import { TopLeftBackButton } from "@src/appV2/redesign/components/TopLeftBackButton";
import { convertModalShiftDataToUseBookShiftParams } from "@src/appV2/redesign/Shift/Booking/convertModalShiftDataToUseBookShiftParams";
import { PreBookingCheck, useBookShift } from "@src/appV2/redesign/Shift/Booking/useBookShift";
import { markWorkerAsSeenClipboardScoreEducation } from "@src/appV2/redesign/Shift/Booking/useShouldShowClipboardScoreEducation";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
import { SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { ArriveOnTimeStep } from "./ArriveOnTimeStep";
import { ConfirmStep } from "./ConfirmStep";
import { EarnPointsStep } from "./EarnPointsStep";
import { FinalRecapStep } from "./FinalRecapStep";
import { HigherScoreIsBetterStep } from "./HigherScoreIsBetterStep";
import { IntroStep } from "./IntroStep";
import { LifeHappensStep } from "./LifeHappensStep";
import { RecapStep } from "./RecapStep";
import { StartRecapStep } from "./StartRecapStep";
import {
  type EducationAnalyticsPayload,
  logCSEducationFlowStep,
  setNextEducationStep,
  setPreviousEducationStep,
} from "./useEducationAnalytics";
import { YourScoreStep } from "./YourScoreStep";

export type EducationStep =
  | "intro"
  | "your-score"
  | "higher-score-is-better"
  | "earn-points"
  | "life-happens"
  | "arrive-on-time"
  | "start-recap"
  | "recap-first"
  | "recap-second"
  | "recap-third"
  | "final-confirm";

const PROGRESS_STEPS: EducationStep[] = [
  "higher-score-is-better",
  "earn-points",
  "life-happens",
  "arrive-on-time",
  "start-recap",
  "recap-first",
  "recap-second",
  "recap-third",
];

const getProgressStepIndex = (step: EducationStep): number => {
  return PROGRESS_STEPS.indexOf(step);
};

const themeForStep = (theme: Theme, step: EducationStep) => {
  // FIXME: Automatically disabled during ESLint config update
  switch (step) {
    case "your-score": {
      return theme.palette.onboarding?.inProgress.gradient;
    }

    default: {
      return theme.palette.onboarding?.neutral.gradient;
    }
  }
};

export interface ClipboardScoreEducationFlowContainerProps {
  readonly modalState: UseModalState;
  readonly shift: ModalShiftData;
  readonly rateNegotiationId: string | undefined;
}

export function ClipboardScoreEducationFlowContainer(
  props: ClipboardScoreEducationFlowContainerProps
) {
  const { modalState, shift, rateNegotiationId } = props;

  const history = useHistory();
  const { navigateToModal } = useShiftModalsDataContext();

  const {
    id: shiftId,
    offer,
    attributes: { qualification },
  } = shift;

  const { id: shiftOfferId } = offer ?? {};

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_CONFIRM_BOOKING_MODAL_PATH, {
        pathParams: { shiftId },
      });
    },
    rateNegotiationId,
  });

  const { attemptBookingShift } = useBookShift(bookShiftParams);

  const [step, setStep] = useState<EducationStep>("intro");
  const worker = useDefinedWorker();
  const { data: clipboardScoreProfile, isLoading } = useGetClipboardScoreProfile(worker.userId);

  // Track initial intro step when modal opens
  useEffect(() => {
    logCSEducationFlowStep("intro", {
      shiftId,
      shiftOfferId,
      workerId: worker.userId,
      action: "viewed",
    });
  }, [shiftOfferId, shiftId, worker.userId]);

  const segmentEventPayload: EducationAnalyticsPayload = {
    shiftId,
    shiftOfferId,
    workerId: worker.userId,
    action: "viewed",
  };

  const createProgressCarouselProps = (currentStep: EducationStep) => {
    if (!PROGRESS_STEPS.includes(currentStep)) {
      return undefined;
    }

    return {
      currentIndex: getProgressStepIndex(currentStep),
      totalSteps: PROGRESS_STEPS.length,
      onIndexChange: (index: number) => {
        const nextStep = PROGRESS_STEPS[index];
        if (nextStep) {
          setStep(nextStep);
        }
      },
    };
  };

  return (
    <FullScreenDialog
      modalState={modalState}
      PaperProps={{
        sx: {
          background: (theme) => themeForStep(theme, step),
        },
      }}
    >
      {isLoading ? (
        <Stack
          spacing={6}
          alignItems="center"
          sx={{
            width: "100%",
            height: "100%",
            justifyContent: "space-between",
            paddingX: 5,
            paddingTop: 8,
            paddingBottom: `calc(16px + env(safe-area-inset-bottom))`,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <TopLeftBackButton
              iconType="chevron-left"
              onClick={() => {
                modalState.closeModal();
                logCSEducationFlowStep("intro", {
                  ...segmentEventPayload,
                  action: "back",
                });
              }}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "background.default",
            }}
          >
            <LoadingSpinner size="large" />
          </Box>

          <Box sx={{ width: "100%" }} />
        </Stack>
      ) : (
        <Box
          sx={{
            height: "100%",
            paddingX: 5,
            paddingTop: 8,
            paddingBottom: `calc(16px + env(safe-area-inset-bottom))`,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "auto",
          }}
        >
          {step === "intro" && (
            <IntroStep
              onNext={() => {
                setNextEducationStep("your-score", segmentEventPayload, setStep);
              }}
              onBack={() => {
                logCSEducationFlowStep("intro", {
                  ...segmentEventPayload,
                  action: "back",
                });
                history.goBack();
              }}
            />
          )}

          {step === "your-score" && (
            <YourScoreStep
              score={clipboardScoreProfile?.clipboardScore ?? 0}
              onNext={() => {
                setNextEducationStep("higher-score-is-better", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep("your-score", "intro", segmentEventPayload, setStep);
              }}
            />
          )}

          {step === "higher-score-is-better" && (
            <HigherScoreIsBetterStep
              shiftQualification={qualification}
              progressCarousel={createProgressCarouselProps("higher-score-is-better")}
              onNext={() => {
                setNextEducationStep("earn-points", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "higher-score-is-better",
                  "your-score",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "earn-points" && (
            <EarnPointsStep
              pointsForWorkingShift={clipboardScoreProfile?.policy?.workShift?.points ?? 0}
              progressCarousel={createProgressCarouselProps("earn-points")}
              onNext={() => {
                setNextEducationStep("life-happens", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "earn-points",
                  "higher-score-is-better",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "life-happens" && (
            <LifeHappensStep
              cancellationPolicy={clipboardScoreProfile?.policy?.cancelShift ?? []}
              progressCarousel={createProgressCarouselProps("life-happens")}
              onNext={() => {
                setNextEducationStep("arrive-on-time", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "life-happens",
                  "earn-points",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "arrive-on-time" && (
            <ArriveOnTimeStep
              pointsForTimeliness={clipboardScoreProfile?.policy?.shiftClockInConfig?.points ?? 0}
              latenessPolicy={clipboardScoreProfile?.policy?.latenessConfig ?? []}
              progressCarousel={createProgressCarouselProps("arrive-on-time")}
              onNext={() => {
                setNextEducationStep("start-recap", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "arrive-on-time",
                  "life-happens",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "start-recap" && (
            <StartRecapStep
              progressCarousel={createProgressCarouselProps("start-recap")}
              onNext={() => {
                setNextEducationStep("recap-first", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "start-recap",
                  "arrive-on-time",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "recap-first" && (
            <RecapStep
              stepNumber={1}
              totalPages={3}
              primaryText="You earn points by working shifts and arriving on time."
              secondaryText="A higher score = better access."
              buttonText="Next"
              progressCarousel={createProgressCarouselProps("recap-first")}
              onNext={() => {
                setNextEducationStep("recap-second", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "recap-first",
                  "start-recap",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "recap-second" && (
            <RecapStep
              stepNumber={2}
              totalPages={3}
              primaryText="Maintaining a high score builds you a buffer in the event that life happens."
              secondaryText="If plans change, try to cancel >24h in advance."
              buttonText="Next"
              progressCarousel={createProgressCarouselProps("recap-second")}
              onNext={() => {
                setNextEducationStep("recap-third", segmentEventPayload, setStep);
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "recap-third",
                  "recap-first",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {step === "recap-third" && (
            <FinalRecapStep
              stepNumber={3}
              totalPages={3}
              primaryText={
                <>
                  <Text
                    variant="h4"
                    textAlign="left"
                    color={(theme) => theme.palette.error.main}
                    sx={{
                      fontSize: "24px",
                      lineHeight: "29px",
                      fontWeight: 500,
                    }}
                  >
                    Keep your Score above zero
                  </Text>{" "}
                  to stay in good standing and keep full access to shifts!
                </>
              }
              secondaryText=""
              progressCarousel={createProgressCarouselProps("recap-third")}
              onConfirm={() => {
                logCSEducationFlowStep("recap-third", {
                  ...segmentEventPayload,
                  action: "confirmed",
                });
                markWorkerAsSeenClipboardScoreEducation(worker.userId);
                setNextEducationStep("final-confirm", segmentEventPayload, setStep);
              }}
              onReview={() => {
                setStep("intro");
              }}
              onBack={() => {
                setPreviousEducationStep(
                  "recap-third",
                  "recap-second",
                  segmentEventPayload,
                  setStep
                );
              }}
            />
          )}

          {/* Non-carousel step */}

          {step === "final-confirm" && (
            <ConfirmStep
              onConfirm={async () => {
                await attemptBookingShift({
                  skipChecks: [PreBookingCheck.CLIPBOARD_SCORE_EDUCATION],
                });
              }}
              onBack={() => {
                logCSEducationFlowStep("final-confirm", {
                  ...segmentEventPayload,
                  action: "back",
                });
                setStep("recap-third");
              }}
            />
          )}
        </Box>
      )}
    </FullScreenDialog>
  );
}
